import React, {useEffect, useState} from "react";
import {
	collection,
	getDocs,
	orderBy,
	query,
	where,
	limit,
	startAfter,
	getCountFromServer,
} from "firebase/firestore";

import {toTitleCase} from "../utils/format";

import {db} from "../firebase";
import {useActiveChild} from "./ActiveChildContext";
import {
	WaBreadcrumb,
	WaBreadcrumbItem,
	WaButton,
	WaIcon,
	WaSelect,
	WaOption,
} from "@web.awesome.me/webawesome-pro/dist/react";
import {LayoutGrid} from "lucide-react";
import Header from "./Header";
import moment from "moment";

const FeedListPage = () => {
	const [feedEntries, setFeedEntries] = useState([]);
	const [totalFeedCount, setTotalFeedCount] = useState(0);
	const [firstCursor, setFirstCursor] = useState(null);
	const [lastCursor, setLastCursor] = useState(null);
	const [paginationLimit, setPaginationLimit] = useState(10);
	const [pageStart, setPageStart] = useState(0);
	const {activeChild, activeChildId} = useActiveChild();

	const fetchFeedEntries = async () => {
		if (!activeChildId) return;
		const feedQuery = query(
			collection(db, "feed"),
			where("child_id", "==", activeChildId),
			orderBy("timestamp", "desc"),
			limit(paginationLimit),
		);
		const querySnapshot = await getDocs(feedQuery);
		const entries = querySnapshot.docs.map((doc) => ({id: doc.id, ...doc.data()}));
		setFeedEntries(entries);
		setPageStart(0);
		setFirstCursor(querySnapshot.docs[0] ?? null);
		setLastCursor(querySnapshot.docs[querySnapshot.docs.length - 1] ?? null);

		const countQuery = query(collection(db, "feed"), where("child_id", "==", activeChildId));
		const countSnapshot = await getCountFromServer(countQuery);
		setTotalFeedCount(countSnapshot.data().count);
	};

	const handlePagination = async (direction) => {
		if (!activeChildId) return;
		let feedQuery;
		if (direction === "next" && lastCursor) {
			feedQuery = query(
				collection(db, "feed"),
				where("child_id", "==", activeChildId),
				orderBy("timestamp", "desc"),
				startAfter(lastCursor),
				limit(paginationLimit),
			);
		} else if (direction === "prev" && firstCursor) {
			feedQuery = query(
				collection(db, "feed"),
				where("child_id", "==", activeChildId),
				orderBy("timestamp", "asc"),
				startAfter(firstCursor),
				limit(paginationLimit),
			);
		} else {
			return;
		}

		const querySnapshot = await getDocs(feedQuery);
		const docs = querySnapshot.docs;
		const entries = (direction === "prev" ? [...docs].reverse() : docs).map((doc) => ({
			id: doc.id,
			...doc.data(),
		}));
		if (entries.length === 0) return;

		if (direction === "next") {
			setPageStart((prev) => prev + feedEntries.length);
		} else {
			setPageStart((prev) => prev - entries.length);
		}

		setFeedEntries(entries);
		setFirstCursor(direction === "prev" ? docs[docs.length - 1] : docs[0]);
		setLastCursor(direction === "prev" ? docs[0] : docs[docs.length - 1]);
	};

	useEffect(() => {
		fetchFeedEntries();
	}, [activeChildId, paginationLimit]);

	return (
		<div className="page">
			<Header
				activePage="feed"
				title="Feed History"
			/>

			<div className="page-meta">
				<WaBreadcrumb>
					<WaBreadcrumbItem href="/">
						<LayoutGrid size={24} />
					</WaBreadcrumbItem>
					<WaBreadcrumbItem href={`/feed-history/`}>
						{activeChild ? activeChild.nickname + "'s" : "Select Child for"} Feeds
					</WaBreadcrumbItem>
				</WaBreadcrumb>
			</div>
			<div className="page-content">
				<WaButton
					className="btn-gloss"
					href="/feed/"
				>
					<WaIcon
						family="default"
						name="plus"
						slot="start"
					/>
					Add Feed
				</WaButton>
				<div className="table-scroll-wrapper">
					<table className="feed-table scroll has-pagination">
						<thead>
							<tr>
								<th>Time</th>
								<th>Type</th>
								<th>Amount</th>
								<th>Note</th>
								<th className="sticky-right"></th>
							</tr>
						</thead>
						<tbody>
							{feedEntries.length === 0 ? (
								<tr>
									<td colSpan="5">No feed entries found</td>
								</tr>
							) : (
								feedEntries.map((entry) => (
									<tr key={entry.id}>
										<td>
											<span className="no-wrap">
												{moment(entry.timestamp.toDate()).format("h:mm a")}
											</span>
											<br />
											<small className="text-gloss text-uppercase">
												{moment(entry.timestamp.toDate()).format("MMM D")}
											</small>
										</td>
										<td>{toTitleCase(entry.type)}</td>
										<td>
											{entry.type === "breast"
												? entry.duration + " mins"
												: entry.amount + " ml"}
										</td>
										<td>{entry.note}</td>
										<td className="sticky-right">
											<a href={`/feed/${entry.id}`}>
												<WaButton
													className="btn-gloss btn-round"
													size="medium"
												>
													<WaIcon
														name="edit"
														family="default"
													/>
												</WaButton>
											</a>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
					<footer className="pagination justify-between">
						<div>
							{totalFeedCount === 0 ? 0 : pageStart + 1} {" - "}
							{pageStart + feedEntries.length} of {totalFeedCount}
						</div>
						<div className="elem-group gap-sm">
							<WaSelect
								size="small"
								value={paginationLimit}
								onChange={(e) => setPaginationLimit(parseInt(e.target.value))}
								style={{width: "80px", position: "relative", top: "2px"}}
								className="no-label"
							>
								<WaOption
									value={5}
									{...(paginationLimit === 5 && {selected: true})}
								>
									5
								</WaOption>
								<WaOption
									value={10}
									{...(paginationLimit === 10 && {selected: true})}
								>
									10
								</WaOption>
								<WaOption
									value={20}
									{...(paginationLimit === 20 && {selected: true})}
								>
									20
								</WaOption>
							</WaSelect>
							<WaButton
								className="btn-gloss"
								size="small"
								onClick={() => handlePagination("prev")}
								disabled={pageStart === 0}
							>
								<WaIcon
									name="chevron-left"
									family="default"
								/>
							</WaButton>
							<WaButton
								className="btn-gloss"
								size="small"
								onClick={() => handlePagination("next")}
								disabled={pageStart + feedEntries.length >= totalFeedCount}
							>
								<WaIcon
									name="chevron-right"
									family="default"
								/>
							</WaButton>
						</div>
					</footer>
				</div>
			</div>
		</div>
	);
};

export default FeedListPage;
